/* Theme provider — toggles ``html.theme-light`` (dark is the unclassed
 * default), persists the choice in localStorage, and exposes a hook
 * (``useTheme``) so any component can read or flip it.
 *
 * Initial application happens in two places:
 *   1. A synchronous script in ``index.html`` reads ``localStorage`` and
 *      applies the class before React mounts, so the page never flashes
 *      the wrong theme on reload.
 *   2. This provider re-syncs on mount so SSR / client mismatch can't
 *      leave stale state.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";

export type ThemeMode = "dark" | "light";

interface ThemeContextValue {
  theme: ThemeMode;
  setTheme: (next: ThemeMode) => void;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = "agencygrid:theme";

function readSavedTheme(): ThemeMode {
  if (typeof window === "undefined") return "dark";
  const v = window.localStorage.getItem(STORAGE_KEY);
  if (v === "light" || v === "dark") return v;
  // No saved preference — fall back to OS choice. ``prefers-color-scheme``
  // returns "light" / "dark" / "no-preference"; we honour explicit
  // requests and default to dark otherwise (the brand baseline).
  if (window.matchMedia?.("(prefers-color-scheme: light)").matches) return "light";
  return "dark";
}

function applyTheme(mode: ThemeMode) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (mode === "light") root.classList.add("theme-light");
  else root.classList.remove("theme-light");
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeMode>(() => readSavedTheme());

  // Re-apply on mount in case the inline boot script in index.html
  // didn't run (older browsers / extensions). Idempotent.
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback((next: ThemeMode) => {
    setThemeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Private-browsing / storage-disabled: theme just resets next reload.
    }
    applyTheme(next);
  }, []);

  const toggle = useCallback(() => {
    setTheme(theme === "light" ? "dark" : "light");
  }, [theme, setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used inside <ThemeProvider>");
  }
  return ctx;
}
